import { Meeting, MeetingSession, MeetingSessionMedia } from "@kalyzee/kast-web-sdk/websocket";
import React from "react";
import { ClientContainersIteration } from "../../helpers/bridge/common";
import { SecondaryScreenLayoutVariant } from "../../interfaces/secondaryScreens";
import { DebugPane, DebugPaneProps, DebugRow, DebugSection } from "./DebugPane";

type CarouselContainer = ClientContainersIteration["containers"][0];
type IterationStatus = "displayed" | "preloaded";

const STATUS_COLORS: Record<IterationStatus, string> = {
  displayed: "#4caf50",
  preloaded: "#ff9800",
};

const ON_OFF = (on: boolean | undefined | null) => (
  <span style={{ color: on ? "#4caf50" : "#f44336" }}>{on ? "connected" : "disconnected"}</span>
);

const COLORED = (value: React.ReactNode | undefined | null, on: boolean) => (
  <span style={{ color: on ? "#4caf50" : "#f44336" }}>{value ?? "null"}</span>
);

const renderMediaLine = (
  media: MeetingSessionMedia,
  sourceIndexes: Record<string, number> | undefined,
  status: IterationStatus,
) => {
  const sourceIndex = sourceIndexes?.[media.id];
  const isLocal = sourceIndex !== undefined;
  return (
    <div key={`${media.id}-${status}`} style={{ fontSize: "0.9em" }}>
      <span style={{ color: "#4caf50" }}>[{media.id.slice(0, 8)}]</span>
      {' : '}
      <span>{isLocal ? `localstream=src[${sourceIndex}]` : 'ovenmedia'}</span>
      {' '}
      <span style={{ color: STATUS_COLORS[status] }}>{status}</span>
    </div>
  );
};

const renderIterationLines = (iter: ClientContainersIteration, status: IterationStatus) =>
  iter.containers.flatMap(container =>
    container.sessions.map(media => renderMediaLine(media, container.sourceIndexes, status))
  );

export interface CarouselContainerDebugPaneProps extends Omit<DebugPaneProps, "children"> {
  container: CarouselContainer;
}

/** Per-column debug shown over a video container (carousel id, group, page). */
export const CarouselContainerDebugPane = ({ container, ...paneProps }: CarouselContainerDebugPaneProps) => (
  <DebugPane {...paneProps}>
    <DebugRow label="Carousel ID" value={container.carouselId} />
    <DebugRow label="Group ID" value={container.groupId} />
    <DebugRow label="Page" value={`${container.page + 1} / ${container.pageCount}`} />
  </DebugPane>
);

export interface CarouselGlobalDebugPaneProps extends Omit<DebugPaneProps, "children"> {
  bridge: unknown | null | undefined;
  meeting: Meeting | null | undefined;
  session: MeetingSession | null | undefined;
  sessions: MeetingSession[] | null | undefined;
  layoutVariant: SecondaryScreenLayoutVariant | undefined;
  iteration: ClientContainersIteration;
  pendingIteration: ClientContainersIteration | null | undefined;
}

/** Page-level slave debug summary (bridge state, current/pending iterations). */
export const CarouselGlobalDebugPane = ({
  bridge,
  meeting,
  session,
  sessions,
  layoutVariant,
  iteration,
  pendingIteration,
  ...paneProps
}: CarouselGlobalDebugPaneProps) => (
  <DebugPane {...paneProps}>
    <DebugRow label="Bridge" value={ON_OFF(!!bridge)} />
    <DebugRow
      label="Meeting"
      value={COLORED(meeting ? `${meeting.id?.slice(0, 8)} (${meeting.state})` : null, !!meeting)}
    />
    <DebugRow label="Session" value={COLORED(session ? session.id?.slice(0, 8) : null, !!session)} />
    <DebugRow label="Sessions" value={sessions?.length ?? 0} />
    <DebugRow label="Variant" value={JSON.stringify(layoutVariant)} />
    <DebugSection style={{ marginTop: 8 }}>
      {renderIterationLines(iteration, "displayed")}
      {pendingIteration && renderIterationLines(pendingIteration, "preloaded")}
    </DebugSection>
  </DebugPane>
);
